import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Unstable_Grid2 as Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";

import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useOrganization } from "..";
import { useAuth } from "../../../../contexts/AuthContext";

import { fetchData } from "../../../../fetchData";
import {
  organizationsFinancialControlsLabels,
  organizationsDeliveryWaysLabels,
} from "../../../../utils/labels";

type FormValues = {
  forma_envio: string;
  controle_financeiro: string;
  sistema: string;
  login: string;
  password: string;
};

const OrganizationContabilTab = () => {
  const auth = useAuth();
  const snackbar = useSnackbar();
  const { data: organization, set } = useOrganization();
  const [progress, setProgress] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      forma_envio: organization.contabil.forma_envio || "",
      controle_financeiro: organization.contabil.controle_financeiro || "",
      sistema: organization.contabil.sistema || "",
      login: organization.contabil.login || "",
      password: organization.contabil.password || "",
    },
  });

  async function onSubmit(data: any) {
    setProgress(true);
    const payload = { ...organization, contabil: data };

    try {
      await fetchData(`/organizations/${organization._id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      set(payload);

      setIsEditing(false);
      snackbar?.open(
        "Informações contábeis atualizadas com sucesso!",
        "success",
      );
    } catch (error) {
      snackbar?.open(
        "Ops! No momento não foi possível realizar a ação.",
        "error",
      );
    }

    setProgress(false);
  }

  return (
    <Box sx={{ p: 2 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid xs={12} lg={6}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Forma de Envio de Documentos"
                  size="small"
                  margin="dense"
                  disabled={!isEditing}
                  fullWidth
                  variant="outlined"
                >
                  {organizationsDeliveryWaysLabels.map((label: string) => (
                    <MenuItem key={label} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              control={control}
              name="forma_envio"
            />
          </Grid>
          <Grid xs={12} lg={6}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Controle Financeiro"
                  size="small"
                  margin="dense"
                  disabled={!isEditing}
                  fullWidth
                  variant="outlined"
                >
                  {organizationsFinancialControlsLabels.map((label: string) => (
                    <MenuItem key={label} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              control={control}
              name="controle_financeiro"
            />
          </Grid>
          <Grid xs={12} lg={4}>
            <TextField
              {...register("sistema")}
              label="Nome do Sistema"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={4}>
            <TextField
              {...register("login")}
              label="Usuário"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={4}>
            <TextField
              {...register("password")}
              label="Senha"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
        </Grid>
        {auth?.user.permission_level === "manager" && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              justifyContent={isEditing ? "flex-end" : "flex-start"}
            >
              {!isEditing && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setIsEditing(true);
                  }}
                >
                  <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar
                </Button>
              )}
              {isEditing && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    type="submit"
                  >
                    {!progress && "Salvar"}
                    {progress && (
                      <CircularProgress size={12} sx={{ color: "#FFF" }} />
                    )}
                  </Button>
                </>
              )}
            </Stack>
          </>
        )}
      </form>
    </Box>
  );
};

export default OrganizationContabilTab;
